import React from 'react';
import { TopAnomaly } from '../../types/dashboard';
import { CloudProviderIcon } from '../common/CloudProviderIcon';
import { formatCurrency as formatCurrencyUtil, CurrencyCode } from '../../utils/currency';

/**
 * AnomalyList component for displaying top anomalies
 * AC 1: Display top anomalies
 * REQ-012: Filter anomalies by selected provider when filter is active
 * WCAG 2.1 AA compliant with semantic HTML and keyboard navigation
 */

interface AnomalyListProps {
  anomalies: TopAnomaly[];
  maxItems?: number;
  /** REQ-012: When set, only show anomalies matching this provider. 'all' shows everything. */
  provider?: 'all' | 'aws' | 'azure' | 'gcp';
  /** Currency anomaly deltaAmounts are expressed in - the dashboard's own
   * reporting currency (USD for All Providers/AWS/GCP, INR for Azure-only),
   * same as every other cost figure on this page. This previously always
   * hardcoded 'USD', so an Azure-only anomaly's real INR delta rendered
   * with a $ sign as if it were a USD amount. */
  currency?: string;
  /** Anomalies the API deliberately excluded from `anomalies` by its default
   * filter (informational `new_service` findings - see anomalies.ts's own
   * comment). Without surfacing this, a tenant with real findings sees a bare
   * "No anomalies detected", which reads as "nothing is wrong" rather than
   * "N findings are being hidden from you" - the exact misreading the
   * Anomalies page was already fixed for, but this widget was not. */
  hiddenCount?: number;
}

/**
 * Provider badge configuration
 * REQ-012-HYBRID: Color-coded provider badges
 * AWS = orange (#FF9900), Azure = blue (#0089D6), GCP = green (#34A853)
 */
const providerBadgeConfig: Record<string, { emoji: string; label: string; className: string }> = {
  aws: {
    emoji: '🟠',
    label: 'AWS',
    className: 'bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-700',
  },
  azure: {
    emoji: '🔵',
    label: 'Azure',
    className: 'bg-primary/10 text-primary border-primary/30 dark:bg-primary/10 dark:text-primary dark:border-primary/30',
  },
  gcp: {
    emoji: '🟢',
    label: 'GCP',
    className: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700',
  },
};

export const AnomalyList: React.FC<AnomalyListProps> = ({ anomalies, maxItems = 5, provider, currency = 'USD', hiddenCount = 0 }) => {
  // REQ-012: Client-side filtering by selected provider
  // When provider is 'all' or undefined, show all anomalies
  // When a specific provider is selected, only show that provider's anomalies
  const filteredAnomalies = provider && provider !== 'all'
    ? anomalies.filter((a) => a.provider === provider)
    : anomalies;

  const displayAnomalies = filteredAnomalies.slice(0, maxItems);

  const severityStyles = {
    critical: 'bg-rose-500/10 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
    high: 'bg-orange-500/10 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    medium: 'bg-amber-500/10 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    low: 'bg-sky-500/10 text-sky-600 dark:bg-primary/10 dark:text-primary',
  };

  const statusStyles = {
    open: 'bg-secondary text-foreground dark:bg-secondary dark:text-muted-foreground',
    investigating: 'bg-sky-500/10 text-sky-600 dark:bg-primary/10 dark:text-primary',
    resolved: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    dismissed: 'bg-secondary text-muted-foreground dark:bg-secondary dark:text-muted-foreground/70',
  };

  const formatCurrency = (value: number) => {
    const code = (['USD', 'INR', 'EUR', 'GBP'] as const).includes(currency as CurrencyCode)
      ? (currency as CurrencyCode)
      : 'USD';
    return formatCurrencyUtil(value, code);
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-sm dark:border-border dark:bg-card">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Top Anomalies</h3>
        <a
          href="/anomalies"
          className="text-sm font-medium text-primary hover:text-primary dark:hover:text-primary"
          aria-label="View all anomalies"
        >
          View All →
        </a>
      </div>

      {displayAnomalies.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-muted-foreground">
            {provider && provider !== 'all'
              ? `No anomalies detected for ${provider.toUpperCase()}`
              : 'No anomalies detected'}
          </p>
          {hiddenCount > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {hiddenCount} informational finding{hiddenCount === 1 ? '' : 's'} (first-time
              service appearances) {hiddenCount === 1 ? 'is' : 'are'} hidden by the default
              filter —{' '}
              <a href="/anomalies" className="text-primary underline">
                view all on the Anomalies page
              </a>
            </p>
          )}
        </div>
      ) : (
        <ul className="space-y-3" role="list" aria-label="Top anomalies list">
          {displayAnomalies.map((anomaly) => (
            <li
              key={anomaly.id}
              className="rounded-lg border border-border p-4 transition-colors hover:bg-muted/30 dark:border-border dark:hover:bg-secondary/50"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="mb-2 flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${severityStyles[anomaly.severity]}`}
                      aria-label={`Severity: ${anomaly.severity}`}
                    >
                      {anomaly.severity.toUpperCase()}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${statusStyles[anomaly.status]}`}
                      aria-label={`Status: ${anomaly.status}`}
                    >
                      {anomaly.status.replace('_', ' ').toUpperCase()}
                    </span>
                    {anomaly.provider && providerBadgeConfig[anomaly.provider] && (
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${providerBadgeConfig[anomaly.provider].className}`}
                        aria-label={`Provider: ${providerBadgeConfig[anomaly.provider].label}`}
                      >
                        <CloudProviderIcon provider={anomaly.provider} size={14} />
                        {providerBadgeConfig[anomaly.provider].label}
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {anomaly.title}
                  </h4>
                  <p className="mt-1 text-sm text-muted-foreground truncate">
                    {anomaly.scope}
                  </p>
                  <div className="mt-2 flex items-center gap-4 text-sm">
                    <span className="font-semibold text-rose-600 dark:text-rose-400">
                      +{formatCurrency(anomaly.deltaAmount)}
                    </span>
                    <span className="text-muted-foreground">
                      ({anomaly.deltaPercentage > 0 ? '+' : ''}
                      {anomaly.deltaPercentage.toFixed(1)}%)
                    </span>
                    <span className="text-muted-foreground/70">•</span>
                    <span className="text-muted-foreground">
                      {formatRelativeTime(anomaly.detectedAt)}
                    </span>
                  </div>
                </div>
                <a
                  href={`/anomalies?id=${anomaly.id}`}
                  className="flex-shrink-0 rounded-md px-3 py-1 text-sm font-medium text-primary hover:bg-primary/10 dark:text-primary dark:hover:bg-primary/10"
                  aria-label={`Investigate ${anomaly.title}`}
                >
                  Investigate
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
